/**
 * 数据权限配置页面（统一入口）
 * 左侧：角色选择器  右侧：Tabs（数据范围 + 字段权限 + 脱敏预览）
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Space, Input, Select, Tabs, Tag, List, Empty,
  Modal, message, Spin, Row, Col, Statistic, Typography,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined,
  SafetyOutlined, DatabaseOutlined, EyeInvisibleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { getRoleList } from '../../api/role';
import {
  getRoleDataPermissions, getDataPermissionOptions, getResourceTypes,
  getDataPermissionStatistics, deleteDataScope, deleteFieldPermission,
} from '../../api/dataPermission';
import type {
  Role, DataScopeRule, FieldPermissionRule, DataPermissionStatistics,
} from '../../api/types';
import DataScopeForm from './DataScopeForm';
import FieldPermissionForm from './FieldPermissionForm';

const { Text } = Typography;
const { confirm } = Modal;

/** 脱敏预览：对示例数据应用脱敏规则 */
const applyMask = (value: string, maskType?: string, maskPattern?: string): string => {
  if (maskPattern) return maskPattern;
  switch (maskType) {
    case 'phone':
      return value.length >= 11 ? `${value.slice(0, 3)}****${value.slice(-4)}` : '***';
    case 'email':
      return value.includes('@') ? `${value[0]}***@${value.split('@')[1]}` : '***';
    case 'id_card':
      return value.length >= 18 ? `${value.slice(0, 6)}********${value.slice(-4)}` : '***';
    case 'name':
      return value.length >= 2 ? `${value[0]}${'*'.repeat(value.length - 1)}` : '*';
    case 'bank_card':
      return value.length >= 16 ? `${value.slice(0, 4)} **** **** ${value.slice(-4)}` : '***';
    case 'full':
      return '******';
    default:
      return '***';
  }
};

/** 示例数据用于脱敏预览 */
const SAMPLE_DATA: Record<string, string> = {
  phone: '13800138000',
  email: 'zhangsan@example.com',
  id_card: '110101199001011234',
  name: '张三丰',
  bank_card: '6222021234567890123',
  address: '北京市朝阳区建国路88号',
};

const DataPermissionConfig: React.FC = () => {
  // 角色列表
  const [roles, setRoles] = useState<Role[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [roleSearchKeyword, setRoleSearchKeyword] = useState('');

  // 角色的数据权限
  const [scopes, setScopes] = useState<DataScopeRule[]>([]);
  const [fields, setFields] = useState<FieldPermissionRule[]>([]);
  const [permLoading, setPermLoading] = useState(false);

  // 统计
  const [statistics, setStatistics] = useState<DataPermissionStatistics | null>(null);

  // 选项
  const [scopeTypeOptions, setScopeTypeOptions] = useState<{ value: string; label: string }[]>([]);
  const [permissionTypeOptions, setPermissionTypeOptions] = useState<{ value: string; label: string }[]>([]);
  const [maskTypeOptions, setMaskTypeOptions] = useState<{ value: string; label: string }[]>([]);
  const [resourceTypes, setResourceTypes] = useState<{ value: string; label: string; fields?: string[] }[]>([]);

  // 表单 Drawer
  const [scopeFormVisible, setScopeFormVisible] = useState(false);
  const [editingScope, setEditingScope] = useState<DataScopeRule | null>(null);
  const [fieldFormVisible, setFieldFormVisible] = useState(false);
  const [editingField, setEditingField] = useState<FieldPermissionRule | null>(null);

  const [activeTab, setActiveTab] = useState('scopes');

  /** 获取角色列表 */
  const fetchRoles = useCallback(async () => {
    setRolesLoading(true);
    try {
      const res: any = await getRoleList({ current: 1, pageSize: 200 });
      const data = res?.data?.data || res?.data;
      const list = Array.isArray(data) ? data : data?.list || [];
      setRoles(list);
    } catch {
      setRoles([]);
    } finally {
      setRolesLoading(false);
    }
  }, []);

  /** 获取选项数据 */
  const fetchOptions = useCallback(async () => {
    try {
      const [optionsRes, resourceTypesRes]: any[] = await Promise.all([
        getDataPermissionOptions(),
        getResourceTypes(),
      ]);

      const optData = optionsRes?.data?.data || optionsRes?.data;
      if (optData) {
        setScopeTypeOptions(optData.scopeTypes ?? optData.scope_types ?? []);
        setPermissionTypeOptions(optData.permissionTypes ?? optData.permission_types ?? []);
        setMaskTypeOptions(optData.maskTypes ?? optData.mask_types ?? []);
      }

      const resData = resourceTypesRes?.data?.data || resourceTypesRes?.data;
      const types = resData?.types || [];
      setResourceTypes(types.map((t: any) => ({ value: t.value, label: t.label, fields: t.fields })));
    } catch {
      // silent
    }
  }, []);

  /** 获取统计 */
  const fetchStatistics = useCallback(async () => {
    try {
      const res: any = await getDataPermissionStatistics();
      const data = res?.data?.data || res?.data;
      setStatistics(data);
    } catch {
      // silent
    }
  }, []);

  /** 获取角色的数据权限 */
  const fetchRolePermissions = useCallback(async (roleId: number) => {
    setPermLoading(true);
    try {
      const res: any = await getRoleDataPermissions(roleId);
      const data = res?.data?.data || res?.data;
      setScopes(data?.scopes || []);
      setFields(data?.fields || []);
    } catch {
      setScopes([]);
      setFields([]);
    } finally {
      setPermLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
    fetchOptions();
    fetchStatistics();
  }, [fetchRoles, fetchOptions, fetchStatistics]);

  useEffect(() => {
    if (selectedRoleId) {
      fetchRolePermissions(selectedRoleId);
    } else {
      setScopes([]);
      setFields([]);
    }
  }, [selectedRoleId, fetchRolePermissions]);

  /** 选中角色名称 */
  const selectedRole = roles.find((r) => Number(r.id) === selectedRoleId);

  /** 过滤角色列表 */
  const filteredRoles = roleSearchKeyword
    ? roles.filter(
        (r) =>
          r.name.toLowerCase().includes(roleSearchKeyword.toLowerCase()) ||
          r.code?.toLowerCase().includes(roleSearchKeyword.toLowerCase()),
      )
    : roles;

  // ===== Scope table helpers =====
  const getScopeTypeColor = (t: string) => {
    const m: Record<string, string> = { all: 'green', self: 'blue', department: 'orange', department_tree: 'purple', custom: 'red' };
    return m[t] || 'default';
  };
  const getScopeTypeLabel = (t: string) => scopeTypeOptions.find((o) => o.value === t)?.label || t;
  const getResourceTypeLabel = (t: string) => resourceTypes.find((o) => o.value === t)?.label || t;
  const getPermissionTypeColor = (t: string) => {
    const m: Record<string, string> = { visible: 'green', hidden: 'red', readonly: 'blue', editable: 'purple', masked: 'orange' };
    return m[t] || 'default';
  };
  const getPermissionTypeLabel = (t: string) => permissionTypeOptions.find((o) => o.value === t)?.label || t;
  const getMaskTypeLabel = (t: string) => maskTypeOptions.find((o) => o.value === t)?.label || t;

  // ===== Scope CRUD =====
  const handleDeleteScope = (id: number) => {
    confirm({
      title: '确定删除此数据范围规则？',
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        try {
          await deleteDataScope(id);
          message.success('删除成功');
          if (selectedRoleId) fetchRolePermissions(selectedRoleId);
          fetchStatistics();
        } catch {
          message.error('删除失败');
        }
      },
    });
  };

  const handleDeleteField = (id: number) => {
    confirm({
      title: '确定删除此字段权限规则？',
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        try {
          await deleteFieldPermission(id);
          message.success('删除成功');
          if (selectedRoleId) fetchRolePermissions(selectedRoleId);
          fetchStatistics();
        } catch {
          message.error('删除失败');
        }
      },
    });
  };

  const handleScopeFormSuccess = () => {
    setScopeFormVisible(false);
    setEditingScope(null);
    if (selectedRoleId) fetchRolePermissions(selectedRoleId);
    fetchStatistics();
  };

  const handleFieldFormSuccess = () => {
    setFieldFormVisible(false);
    setEditingField(null);
    if (selectedRoleId) fetchRolePermissions(selectedRoleId);
    fetchStatistics();
  };

  // ===== Column definitions =====
  const scopeColumns: ColumnsType<DataScopeRule> = [
    { title: '规则名称', dataIndex: 'name', width: 150 },
    {
      title: '资源类型', dataIndex: 'resourceType', width: 120,
      render: (_, r) => getResourceTypeLabel((r as any).resourceType ?? (r as any).resource_type),
    },
    {
      title: '范围类型', dataIndex: 'scopeType', width: 120,
      render: (_, r) => {
        const st = (r as any).scopeType ?? (r as any).scope_type;
        return <Tag color={getScopeTypeColor(st)}>{getScopeTypeLabel(st)}</Tag>;
      },
    },
    { title: '优先级', dataIndex: 'priority', width: 80 },
    {
      title: '状态', dataIndex: 'status', width: 80,
      render: (s) => <Tag color={s === 1 ? 'success' : 'default'}>{s === 1 ? '启用' : '禁用'}</Tag>,
    },
    {
      title: '操作', key: 'action', width: 130,
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => { setEditingScope(record); setScopeFormVisible(true); }}>编辑</Button>
          <Button type="link" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDeleteScope(record.id)}>删除</Button>
        </Space>
      ),
    },
  ];

  const fieldColumns: ColumnsType<FieldPermissionRule> = [
    { title: '规则名称', dataIndex: 'name', width: 140 },
    {
      title: '资源类型', dataIndex: 'resourceType', width: 110,
      render: (_, r) => getResourceTypeLabel((r as any).resourceType ?? (r as any).resource_type),
    },
    {
      title: '字段名', width: 110,
      render: (_, r) => (r as any).fieldName ?? (r as any).field_name,
    },
    {
      title: '权限类型', dataIndex: 'permission', width: 100,
      render: (p) => <Tag color={getPermissionTypeColor(p)}>{getPermissionTypeLabel(p)}</Tag>,
    },
    {
      title: '脱敏类型', width: 100,
      render: (_, r) => {
        const mt = (r as any).maskType ?? (r as any).mask_type;
        return mt ? <Tag color="cyan">{getMaskTypeLabel(mt)}</Tag> : '-';
      },
    },
    { title: '优先级', dataIndex: 'priority', width: 70 },
    {
      title: '状态', dataIndex: 'status', width: 80,
      render: (s) => <Tag color={s === 1 ? 'success' : 'default'}>{s === 1 ? '启用' : '禁用'}</Tag>,
    },
    {
      title: '操作', key: 'action', width: 130,
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => { setEditingField(record); setFieldFormVisible(true); }}>编辑</Button>
          <Button type="link" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDeleteField(record.id)}>删除</Button>
        </Space>
      ),
    },
  ];

  /** 脱敏预览：只展示 masked 类型的字段权限 */
  const maskedFields = fields.filter((f) => f.permission === 'masked');

  return (
    <div>
      {/* 统计卡片 */}
      {statistics && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Card bordered={false}><Statistic title="数据范围规则" value={statistics.total_scopes} prefix={<SafetyOutlined />} /></Card>
          </Col>
          <Col span={6}>
            <Card bordered={false}><Statistic title="字段权限规则" value={statistics.total_field_perms} prefix={<DatabaseOutlined />} /></Card>
          </Col>
          <Col span={6}>
            <Card bordered={false}><Statistic title="涉及资源类型" value={Object.keys(statistics.by_resource || {}).length} valueStyle={{ color: '#1890ff' }} /></Card>
          </Col>
          <Col span={6}>
            <Card bordered={false}><Statistic title="范围类型数" value={Object.keys(statistics.by_scope_type || {}).length} valueStyle={{ color: '#3f8600' }} /></Card>
          </Col>
        </Row>
      )}

      {/* 主内容区：左右布局 */}
      <Row gutter={16}>
        {/* 左侧：角色选择器 */}
        <Col span={6}>
          <Card
            title="选择角色"
            bordered={false}
            styles={{ body: { padding: '12px', maxHeight: 'calc(100vh - 280px)', overflow: 'auto' } }}
            extra={
              <Button type="text" size="small" icon={<ReloadOutlined />} onClick={fetchRoles} />
            }
          >
            <Input.Search
              placeholder="搜索角色"
              allowClear
              size="small"
              onChange={(e) => setRoleSearchKeyword(e.target.value)}
              style={{ marginBottom: 8 }}
            />
            <Spin spinning={rolesLoading}>
              {filteredRoles.length > 0 ? (
                <List
                  size="small"
                  dataSource={filteredRoles}
                  renderItem={(role) => (
                    <List.Item
                      style={{
                        cursor: 'pointer',
                        padding: '8px 12px',
                        background: Number(role.id) === selectedRoleId ? '#e6f4ff' : undefined,
                        borderRadius: 4,
                      }}
                      onClick={() => setSelectedRoleId(Number(role.id))}
                    >
                      <div>
                        <div style={{ fontWeight: Number(role.id) === selectedRoleId ? 600 : 400 }}>
                          {role.name}
                        </div>
                        <Text type="secondary" style={{ fontSize: 12 }}>{role.code}</Text>
                      </div>
                    </List.Item>
                  )}
                />
              ) : (
                <Empty description="无角色数据" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </Spin>
          </Card>
        </Col>

        {/* 右侧：数据权限配置 Tabs */}
        <Col span={18}>
          <Card
            title={selectedRole ? `${selectedRole.name} - 数据权限配置` : '数据权限配置'}
            bordered={false}
          >
            {selectedRoleId ? (
              <Spin spinning={permLoading}>
                <Tabs
                  activeKey={activeTab}
                  onChange={setActiveTab}
                  items={[
                    {
                      key: 'scopes',
                      label: `数据范围 (${scopes.length})`,
                      children: (
                        <div>
                          <div style={{ marginBottom: 12 }}>
                            <Button
                              type="primary"
                              icon={<PlusOutlined />}
                              onClick={() => { setEditingScope(null); setScopeFormVisible(true); }}
                            >
                              新增数据范围
                            </Button>
                          </div>
                          <Table
                            rowKey="id"
                            columns={scopeColumns}
                            dataSource={scopes}
                            size="small"
                            pagination={scopes.length > 10 ? { pageSize: 10, showTotal: (t) => `共 ${t} 条` } : false}
                            scroll={{ x: 800 }}
                          />
                        </div>
                      ),
                    },
                    {
                      key: 'fields',
                      label: `字段权限 (${fields.length})`,
                      children: (
                        <div>
                          <div style={{ marginBottom: 12 }}>
                            <Button
                              type="primary"
                              icon={<PlusOutlined />}
                              onClick={() => { setEditingField(null); setFieldFormVisible(true); }}
                            >
                              新增字段权限
                            </Button>
                          </div>
                          <Table
                            rowKey="id"
                            columns={fieldColumns}
                            dataSource={fields}
                            size="small"
                            pagination={fields.length > 10 ? { pageSize: 10, showTotal: (t) => `共 ${t} 条` } : false}
                            scroll={{ x: 900 }}
                          />
                        </div>
                      ),
                    },
                    {
                      key: 'preview',
                      label: '脱敏预览',
                      children: (
                        <div>
                          {maskedFields.length > 0 ? (
                            <div>
                              <div style={{ marginBottom: 16, color: '#666' }}>
                                以下展示当前角色的脱敏字段规则及其预览效果：
                              </div>
                              <Table
                                rowKey="id"
                                size="small"
                                dataSource={maskedFields}
                                pagination={false}
                                columns={[
                                  {
                                    title: '资源类型', width: 120,
                                    render: (_, r) => getResourceTypeLabel((r as any).resourceType ?? (r as any).resource_type),
                                  },
                                  {
                                    title: '字段名', width: 120,
                                    render: (_, r) => (r as any).fieldName ?? (r as any).field_name,
                                  },
                                  {
                                    title: '脱敏类型', width: 120,
                                    render: (_, r) => {
                                      const mt = (r as any).maskType ?? (r as any).mask_type;
                                      return <Tag color="cyan">{getMaskTypeLabel(mt)}</Tag>;
                                    },
                                  },
                                  {
                                    title: '原始数据（示例）', width: 200,
                                    render: (_, r) => {
                                      const mt = (r as any).maskType ?? (r as any).mask_type;
                                      return <Text>{SAMPLE_DATA[mt] || 'SampleData123'}</Text>;
                                    },
                                  },
                                  {
                                    title: '脱敏效果', width: 200,
                                    render: (_, r) => {
                                      const mt = (r as any).maskType ?? (r as any).mask_type;
                                      const mp = (r as any).maskPattern ?? (r as any).mask_pattern;
                                      const sample = SAMPLE_DATA[mt] || 'SampleData123';
                                      return (
                                        <Text strong style={{ color: '#ff4d4f' }}>
                                          <EyeInvisibleOutlined style={{ marginRight: 4 }} />
                                          {applyMask(sample, mt, mp)}
                                        </Text>
                                      );
                                    },
                                  },
                                ]}
                              />
                            </div>
                          ) : (
                            <Empty
                              description="该角色暂无脱敏规则"
                              image={Empty.PRESENTED_IMAGE_SIMPLE}
                              style={{ marginTop: 40 }}
                            />
                          )}
                        </div>
                      ),
                    },
                  ]}
                />
              </Spin>
            ) : (
              <Empty
                description="请在左侧选择一个角色来配置数据权限"
                style={{ marginTop: 80 }}
              />
            )}
          </Card>
        </Col>
      </Row>

      {/* 数据范围表单 */}
      <DataScopeForm
        visible={scopeFormVisible}
        scope={editingScope}
        scopeTypeOptions={scopeTypeOptions}
        resourceTypes={resourceTypes}
        onCancel={() => { setScopeFormVisible(false); setEditingScope(null); }}
        onSuccess={handleScopeFormSuccess}
      />

      {/* 字段权限表单 */}
      <FieldPermissionForm
        visible={fieldFormVisible}
        permission={editingField}
        permissionTypeOptions={permissionTypeOptions}
        maskTypeOptions={maskTypeOptions}
        resourceTypes={resourceTypes}
        onCancel={() => { setFieldFormVisible(false); setEditingField(null); }}
        onSuccess={handleFieldFormSuccess}
      />
    </div>
  );
};

export default DataPermissionConfig;
